import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import {
  CdkDrag,
  CdkDragEnd,
  CdkDragDrop,
  CdkDragMove,
  CdkDragPreview,
  CdkDropList,
  moveItemInArray,
  transferArrayItem,
} from '@angular/cdk/drag-drop';
import {
  OrgChartDragStateService,
  ROOT_SENTINEL,
} from '../../pages/economic-group-builder/org-chart-drag-state.service';
import { EconomicGroupMemberResponse } from '@interfaces/economic-groups/responses/economic-group.response';
import { EconomicGroupsHttpService } from '@services/http/economic-groups/economic-groups-http.service';
import { ToastHelperService } from '@services/helpers/toast-helper.service';
import { ConfirmationService } from '@src/app/core/services/helpers/confirmation.service';
import { SharedModule } from '@src/app/shared/shared.module';

export interface MemberMoveEvent {
  memberId: string;
  newParentMemberId: string | null;
  orderIndex: number;
}

@Component({
  selector: 'app-group-tree-node',
  standalone: true,
  imports: [CommonModule, SharedModule, CdkDrag, CdkDragPreview, CdkDropList],
  templateUrl: './group-tree-node.component.html',
  styleUrls: ['./group-tree-node.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GroupTreeNodeComponent {
  @Input() member!: EconomicGroupMemberResponse;
  @Input() groupId!: string;
  @Input() isTenant = false;
  @Input() connectedDropListIds: string[] = [];
  @Input() isPendingMember: (memberId: string) => boolean = () => false;

  @Output() memberMoved = new EventEmitter<MemberMoveEvent>();
  @Output() memberRemoved = new EventEmitter<string>();
  @Output() memberDroppedInLibrary = new EventEmitter<EconomicGroupMemberResponse>();

  constructor(
    private readonly groupsService: EconomicGroupsHttpService,
    private readonly toast: ToastHelperService,
    private readonly confirmation: ConfirmationService,
    readonly dragState: OrgChartDragStateService
  ) {}

  private readonly handledMemberDragEndIds = new Set<string>();

  readonly acceptOnlyGroupMembers = (drag: CdkDrag): boolean => this.isGroupMember(drag.data);

  get initials(): string {
    const words = this.member.companyName.trim().split(/\s+/);
    if (words.length >= 2) {
      return (words[0][0] + words[1][0]).toUpperCase();
    }
    return this.member.companyName.substring(0, 2).toUpperCase();
  }

  onChildDragMoved(event: CdkDragMove<EconomicGroupMemberResponse>): void {
    this.dragState.updateFromCursor(
      event.pointerPosition.x,
      event.pointerPosition.y,
      event.source.data.id
    );
  }

  onChildDragEnded(event: CdkDragEnd<EconomicGroupMemberResponse>): void {
    if (this.isOverCompanyLibrary(event.dropPoint.x, event.dropPoint.y)) {
      this.handledMemberDragEndIds.add(event.source.data.id);
      this.dragState.clear();
      this.memberDroppedInLibrary.emit(event.source.data);
      return;
    }

    const nestTargetId = this.dragState.nestTargetId();
    this.dragState.clear();
    if (!nestTargetId) return;

    const dragged = event.source.data;
    this.handledMemberDragEndIds.add(dragged.id);

    if (nestTargetId === ROOT_SENTINEL) {
      this.memberMoved.emit({
        memberId: dragged.id,
        newParentMemberId: null,
        orderIndex: 0,
      });
      return;
    }

    if (dragged.id === nestTargetId) return;

    this.memberMoved.emit({
      memberId: dragged.id,
      newParentMemberId: nestTargetId,
      orderIndex: 0,
    });
  }

  onDropAsChild(event: CdkDragDrop<EconomicGroupMemberResponse[]>): void {
    if (this.isGroupMember(event.item.data) && this.shouldIgnoreMemberDrop(event.item.data.id)) {
      this.dragState.clear();
      return;
    }

    if (this.isCompanyLibraryItem(event.item.data)) {
      return;
    }

    const nestTargetId = this.dragState.nestTargetId();
    this.dragState.clear(); // always clear after reading

    if (nestTargetId) {
      const dragged: EconomicGroupMemberResponse = event.item.data;
      if (nestTargetId === ROOT_SENTINEL) {
        this.memberMoved.emit({
          memberId: dragged.id,
          newParentMemberId: null,
          orderIndex: 0, // builder computes final index in onMemberMoved
        });
        return;
      }
      if (dragged.id !== nestTargetId) {
        this.memberMoved.emit({
          memberId: dragged.id,
          newParentMemberId: nestTargetId,
          orderIndex: 0,
        });
      }
      return;
    }

    if (event.previousContainer === event.container && event.previousIndex === event.currentIndex) {
      return;
    }

    if (event.previousContainer === event.container) {
      moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
    } else {
      transferArrayItem(
        event.previousContainer.data,
        event.container.data,
        event.previousIndex,
        event.currentIndex
      );
    }

    const movedMember = event.container.data[event.currentIndex];
    this.memberMoved.emit({
      memberId: movedMember.id,
      newParentMemberId: this.member.id,
      orderIndex: event.currentIndex,
    });
  }

  confirmRemove(): void {
    if (this.isPendingMember(this.member.id)) return;

    this.confirmation
      .confirm('Remover empresa', `Remover "${this.member.companyName}" do grupo?`)
      .subscribe(confirmed => {
        if (!confirmed) return;
        this.groupsService.removeMember(this.groupId, this.member.id).subscribe({
          next: () => {
            this.toast.showToastSuccess('Empresa removida do grupo.');
            this.memberRemoved.emit(this.member.id);
          },
          error: () => this.toast.showToastError('Erro ao remover empresa.'),
        });
      });
  }

  private isOverCompanyLibrary(cursorX: number, cursorY: number): boolean {
    return this.dragState
      .getElementsFromPoint(cursorX, cursorY)
      .some(element => !!element.closest('[data-company-library]'));
  }

  private isCompanyLibraryItem(value: unknown): value is { id: string; name: string } {
    const item = value as { id?: string; name?: string; companyId?: string };
    return !!item?.id && typeof item.name === 'string' && !item.companyId;
  }

  private isGroupMember(value: unknown): value is EconomicGroupMemberResponse {
    const item = value as EconomicGroupMemberResponse;
    return !!item?.id && typeof item.companyId === 'string';
  }

  private shouldIgnoreMemberDrop(memberId: string): boolean {
    if (!this.handledMemberDragEndIds.has(memberId)) return false;
    this.handledMemberDragEndIds.delete(memberId);
    return true;
  }
}
